/**
 * Ponto de entrada.
 *
 * Sem `StrictMode` de propósito: o duplo-render do modo estrito monta e desmonta
 * cada efeito duas vezes, e aqui os efeitos abrem SOCKETS e SALAS de LiveKit —
 * ligar/desligar/ligar uma sala WebRTC em milissegundos dá reconexões falsas e
 * bugs que só existem em desenvolvimento. Os avisos que ele daria não compensam
 * depurar problemas que a produção não tem.
 */

import { registerSW } from "virtual:pwa-register";
import { createRoot } from "react-dom/client";

import { App } from "./App";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { syncPush } from "./lib/push";
import { initSentry } from "./lib/sentry";
import { applyTheme, loadTheme } from "./lib/theme";
import "./styles.css";

// Captura de erros (no-op sem PUBLIC_SENTRY_DSN; o SDK só é carregado se houver
// DSN). Fire-and-forget — não bloqueia o arranque do render.
void initSentry();

// Regista o service worker do PWA. Em dev é um no-op (devOptions desligado), por
// isso não interfere com o HMR; `immediate` liga a cache mal a página abre.
registerSW({ immediate: true });

// Se o utilizador já concedeu notificações neste dispositivo, re-sincroniza a
// subscrição com o servidor (o endpoint do push pode ter rodado desde a última
// sessão). Silencioso e best-effort — nunca pede permissão nem bloqueia o render.
void syncPush();

const host = document.getElementById("root");
if (!host) throw new Error("Falta o elemento #root no index.html.");

// Aplicar o tema guardado ANTES do primeiro render — senão pisca a paleta por
// omissão até o React montar. O tema é do dispositivo (localStorage), vive fora
// do store e vale mesmo sem sessão.
applyTheme(loadTheme());

// A fronteira mais de fora que existe. Não sabe recuperar de nada — o que ela
// impede é o ecrã BRANCO: sem ela, uma excepção no render sobe até aqui, o React
// desmonta a árvore toda e o que sobra é um `<div id="root">` vazio, sem uma
// palavra a dizer o que se passou nem nada em que carregar.
createRoot(host).render(
  <ErrorBoundary
    zone="root"
    title="O GreenVoice encravou."
    text="Alguma coisa rebentou a desenhar o ecrã. Recarregar resolve quase sempre; se voltar a acontecer no mesmo sítio, é um erro nosso."
  >
    <App />
  </ErrorBoundary>,
);
